import { calendarDate, dueLabel, todayIn } from '../dates';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { graphql } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { staffLabel } from '../components/staff-labels';
import { PageHeader } from '../components/Workspace';

type WorkItem = { orderId: string; organizationName: string; projectReference: string; projectName: string; stateCode: string | null;
  county: string | null; documentType: string; status: string; nextAction: string | null; dueDate: string | null; reason: string | null; updatedAt: string; timeZone: string };
type WorkPage = { items: WorkItem[]; total: number; nextOffset: number | null };
type View = 'NEEDS_STAFF' | 'ALL';

const query = `query Queue($view:String,$search:String,$status:String,$offset:Int){staffWorkQueue(view:$view,search:$search,status:$status,offset:$offset){
  items{orderId organizationName projectReference projectName stateCode county documentType status nextAction dueDate reason updatedAt timeZone} total nextOffset}}`;
const statuses = ['DRAFT', 'MISSING_FACTS', 'RESEARCH', 'FIRST_REVIEW', 'READY_FOR_FULFILLMENT', 'SUBMITTED', 'FULFILLED', 'VENDOR_EXCEPTION',
  'RETURNED_MAIL', 'RECORDING_REJECTED', 'CORRECTION', 'COUNSEL_ESCALATION', 'CANCELLED'];
function ago(timestamp: string) {
  const days = Math.floor((Date.now() - Date.parse(timestamp)) / 86_400_000);
  return days < 1 ? 'Today' : days === 1 ? 'Yesterday' : `${days} days ago`;
}

/**
 * Staff work across every company. "Needs staff" shows only notices waiting on
 * PayRight staff, soonest due first, with why; "All notices" finds any notice.
 * The server filters, sorts and pages, so this stays fast with any number of notices.
 */
export function OperationsQueuePage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState<View>('NEEDS_STAFF');
  const [filters, setFilters] = useState({ search: '', status: '' });
  const [draft, setDraft] = useState({ search: '', status: '' });
  const [page, setPage] = useState<WorkPage | null>(null);
  const [error, setError] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);
  const variables = (offset = 0) => ({ view, search: filters.search || null, status: view === 'ALL' ? filters.status || null : null, offset });

  useEffect(() => {
    if (!token) return;
    let active = true;
    graphql<{ staffWorkQueue: WorkPage }>({ query, variables: variables(), token })
      .then(data => { if (active) { setPage(data.staffWorkQueue); setError(''); } })
      .catch((reason: Error) => { if (active) setError(reason.message); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, view, filters]);

  async function more() {
    if (!token || page?.nextOffset == null) return;
    setLoadingMore(true);
    try {
      const data = await graphql<{ staffWorkQueue: WorkPage }>({ query, variables: variables(page.nextOffset), token });
      setPage(current => current && { ...data.staffWorkQueue, items: [...current.items, ...data.staffWorkQueue.items] });
    } catch (reason) { setError((reason as Error).message); } finally { setLoadingMore(false); }
  }
  function search(event: FormEvent) { event.preventDefault(); setPage(null); setFilters({ ...draft }); }
  function choose(next: View) { if (next !== view) { setPage(null); setView(next); } }

  return <><PageHeader eyebrow="PayRight staff" title="Notice queue" action={<Link className="button button-quiet" to="/ops/help#staff-work">Open notice workflow</Link>} />
    <div className="queue-tabs" role="tablist" aria-label="Queue">
      <button type="button" role="tab" aria-selected={view === 'NEEDS_STAFF'} onClick={() => choose('NEEDS_STAFF')}>Needs staff</button>
      <button type="button" role="tab" aria-selected={view === 'ALL'} onClick={() => choose('ALL')}>All notices</button>
    </div>
    <form className="queue-filters" onSubmit={search} role="search">
      <label>Find a company, job name or reference<input value={draft.search} onChange={event => setDraft({ ...draft, search: event.target.value })} /></label>
      {view === 'ALL' && <label>Status<select value={draft.status} onChange={event => setDraft({ ...draft, status: event.target.value })}>
        <option value="">Any status</option>{statuses.map(status => <option key={status} value={status}>{staffLabel(status)}</option>)}</select></label>}
      <button className="button button-quiet">Search</button>
    </form>
    {error && <div className="alert error" role="alert">{error}</div>}
    {!page ? <div className="loading-screen" role="status">Loading notices…</div>
      : page.items.length === 0 ? <p className="workspace-intro">{view === 'NEEDS_STAFF' && !filters.search ? 'Nothing is waiting on staff right now.' : 'No notices match.'}</p>
        : <><p className="queue-count">{view === 'NEEDS_STAFF' ? `${page.total} ${page.total === 1 ? 'notice needs' : 'notices need'} staff` : `${page.total} ${page.total === 1 ? 'notice' : 'notices'}`}{page.total > page.items.length ? `, showing ${page.items.length}` : ''}</p>
          <div className="data-table" tabIndex={0}><table className="queue-table">
            <thead><tr><th>Due</th><th>Job</th><th>Company</th><th>Location</th><th>Notice</th><th>{view === 'NEEDS_STAFF' ? 'What is needed' : 'Status'}</th><th>Last change</th></tr></thead>
            <tbody>{page.items.map(item => <tr key={item.orderId} className="queue-row" onClick={() => navigate(`/ops/orders/${item.orderId}`)}>
              <td data-label="Due">{item.dueDate ? calendarDate(item.dueDate, 'weekday') : '—'}<small>{dueLabel(item.dueDate, todayIn(item.timeZone))}</small></td>
              <td data-label="Job"><Link to={`/ops/orders/${item.orderId}`} onClick={event => event.stopPropagation()}>{item.projectReference}</Link><small>{item.projectName}</small></td>
              <td data-label="Company">{item.organizationName}</td>
              <td data-label="Location">{[item.county, item.stateCode].filter(Boolean).join(', ') || '—'}</td>
              <td data-label="Notice">{staffLabel(item.documentType)}</td>
              <td data-label={view === 'NEEDS_STAFF' ? 'What is needed' : 'Status'} className="queue-need">{item.reason ?? staffLabel(item.status)}{item.nextAction && <small>{item.nextAction}</small>}</td>
              <td data-label="Last change">{ago(item.updatedAt)}</td>
            </tr>)}</tbody></table></div>
          {page.nextOffset != null && <button type="button" className="button button-quiet" disabled={loadingMore} onClick={() => void more()}>{loadingMore ? 'Loading…' : 'Show more'}</button>}</>}
  </>;
}
